import React from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const PremisesLiability = () => {
  return (
    <div data-testid="premises-liability-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-6 tracking-tight">
            Premises Liability Cases
          </h1>
          <p className="text-base md:text-xl text-slate-600 leading-relaxed">
            Property owners have a duty to maintain safe conditions. When they fail to do so and someone 
            is injured, they must be held accountable. We represent clients injured on unsafe premises 
            throughout North Carolina and South Carolina.
          </p>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            How We Help
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Investigation of the dangerous condition and how long it existed</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Gathering evidence of notice to the property owner (actual or constructive)</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Expert analysis of property maintenance standards and safety violations</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Understanding of NC contributory negligence and SC comparative fault rules</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Documentation of your injuries and all related losses</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Aggressive pursuit of compensation from property owners and their insurers</span>
            </p>
          </div>
        </div>
      </section>

      {/* State-Specific Rules */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            State-Specific Fault Rules
          </h2>
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 p-8">
              <h3 className="text-2xl font-heading font-semibold text-primary mb-3">
                North Carolina: Contributory Negligence
              </h3>
              <p className="text-slate-600 leading-relaxed">
                North Carolina follows contributory negligence, which means if you are found even 1% at fault 
                for your injury, you may be barred from recovery. This strict rule makes it especially important 
                to have experienced legal representation. We know how to build a strong case demonstrating that 
                the property owner's negligence—not your actions—caused your injury.
              </p>
            </div>
            <div className="bg-white border border-slate-200 p-8">
              <h3 className="text-2xl font-heading font-semibold text-primary mb-3">
                South Carolina: Modified Comparative Fault
              </h3>
              <p className="text-slate-600 leading-relaxed">
                South Carolina uses modified comparative fault, which is less harsh. If you are found partially 
                at fault, your recovery is reduced by your percentage of fault, as long as you are not more than 
                50% responsible. We work to minimize any attribution of fault to you and maximize your compensation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Do Next */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            After a Premises Injury
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Document the Scene
              </h3>
              <p className="text-slate-600 leading-relaxed">
                If possible, take photos or videos of the dangerous condition that caused your injury. Get the 
                names and contact information of witnesses. Report the incident to the property owner or manager.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Seek Medical Care
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Get medical attention right away, even for what seems like a minor injury. Medical records are 
                critical evidence in your case.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Preserve Evidence
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Keep all medical bills, photographs, and any correspondence. Contact us early so we can help 
                preserve critical evidence before it is lost or destroyed.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Do Not Accept Quick Settlements
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Property owners and their insurance companies may offer quick settlements that do not reflect 
                the full value of your claim. Consult with us before accepting any offer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-12 bg-primary text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <p className="text-lg">
            Representing premises liability clients across <strong>North Carolina</strong> and <strong>South Carolina</strong>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-1">
                What types of accidents fall under premises liability?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Common premises liability cases include slip and fall accidents, inadequate security leading to assault, 
                falling objects, unsafe stairs or railings, inadequate lighting, and swimming pool accidents.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-2">
                Does it matter if the property is a business or private residence?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Yes. The duty of care owed by the property owner depends on your status (invitee, licensee, or trespasser) 
                and the type of property. Business owners generally owe a higher duty to customers than homeowners owe to 
                social guests. We analyze your specific situation to determine liability.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-3">
                What if the property owner says the hazard was "open and obvious"?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                This is a common defense. However, just because a condition may have been visible does not automatically 
                relieve the property owner of liability. The specifics matter. We know how to counter this defense effectively.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-4">
                How long do I have to file a claim?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Generally, you have three years from the date of injury in both North Carolina and South Carolina. 
                However, it's best to contact us immediately. Evidence can disappear, and the sooner we start, 
                the stronger your case will be.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-slate-900 mb-6 tracking-tight">
            Hold Property Owners Accountable
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Contact us for a free consultation about your premises liability case.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-primary text-white px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm shadow-sm hover:bg-primary/90 transition-colors"
              data-testid="cta-consultation"
            >
              Request a Consultation
            </Link>
            <a
              href="tel:+13363541040"
              className="border-2 border-primary text-primary px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm hover:bg-primary/10 transition-colors"
              data-testid="cta-call"
            >
              Call 336-354-1040
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PremisesLiability;